/**
 * ProductGroupsSettings — קבוצות מוצרים
 *
 * Tree-view CRUD for inventory_product_groups.
 * Supports unlimited nesting: top-level groups + sub-groups.
 *
 * Features:
 *  • Expandable / collapsible tree rows
 *  • Add top-level group
 *  • Add sub-group under any existing group
 *  • Edit any group's name
 *  • Delete (CASCADE removes all children via DB)
 *  • Toggle use_as_b2b_filter per group (Phase 92)
 */

import { useEffect, useState, useCallback } from 'react';
import { FolderTree, Plus, Pencil, Trash2, X, Loader as Loader2, CircleAlert as AlertCircle, ChevronDown, ChevronLeft, FolderOpen, Folder, FolderPlus, ListFilter as Filter } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProductGroup {
  id:                string;
  company_id:        string;
  parent_id:         string | null;
  name:              string;
  use_as_b2b_filter: boolean;
  created_at:        string;
  updated_at:        string;
  children?:         ProductGroup[];
}

interface Props {
  companyId: string;
  showToast: (msg: string, ok?: boolean) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Build a nested tree from a flat list */
function buildTree(flat: ProductGroup[]): ProductGroup[] {
  const map = new Map<string, ProductGroup>();
  flat.forEach(g => map.set(g.id, { ...g, children: [] }));

  const roots: ProductGroup[] = [];
  map.forEach(g => {
    if (g.parent_id && map.has(g.parent_id)) {
      map.get(g.parent_id)!.children!.push(g);
    } else {
      roots.push(g);
    }
  });

  // Sort each level alphabetically
  const sortLevel = (nodes: ProductGroup[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name, 'he'));
    nodes.forEach(n => sortLevel(n.children ?? []));
  };
  sortLevel(roots);
  return roots;
}

// ─── Single tree-node row ─────────────────────────────────────────────────────

interface NodeProps {
  node:        ProductGroup;
  depth:       number;
  expanded:    Set<string>;
  toggling:    Set<string>;
  toggle:      (id: string) => void;
  onAddSub:    (parent: ProductGroup) => void;
  onEdit:      (group: ProductGroup) => void;
  onDelete:    (group: ProductGroup) => void;
  onToggleB2B: (group: ProductGroup) => void;
}

function GroupNode({
  node, depth, expanded, toggling, toggle,
  onAddSub, onEdit, onDelete, onToggleB2B,
}: NodeProps) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isOpen      = expanded.has(node.id);
  const isToggling  = toggling.has(node.id);

  return (
    <>
      <tr className="hover:bg-gray-50/70 transition-colors group">
        {/* Group name + expand */}
        <td className="py-2.5 pr-4 pl-2">
          <div
            className="flex items-center gap-1"
            style={{ paddingRight: `${depth * 20}px` }}
          >
            <button
              onClick={() => toggle(node.id)}
              className={`p-0.5 rounded transition-colors text-gray-400
                ${hasChildren ? 'hover:text-gray-700' : 'invisible pointer-events-none'}`}
            >
              {isOpen
                ? <ChevronDown  className="w-4 h-4" />
                : <ChevronLeft  className="w-4 h-4" />}
            </button>

            {isOpen
              ? <FolderOpen className="w-4 h-4 text-amber-500 shrink-0" />
              : <Folder     className="w-4 h-4 text-amber-400 shrink-0" />}

            <span className="text-sm font-medium text-gray-800 mr-1">{node.name}</span>

            {hasChildren && (
              <span className="text-xs text-gray-400 mr-1">
                ({node.children!.length})
              </span>
            )}

            {node.use_as_b2b_filter && (
              <span
                title="משמש כמסנן ב-B2B"
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full
                           bg-blue-50 text-blue-500 text-[10px] font-semibold border border-blue-100"
              >
                <Filter className="w-2.5 h-2.5" />
                B2B
              </span>
            )}
          </div>
        </td>

        {/* B2B Filter toggle */}
        <td className="py-2.5 px-3 w-36">
          <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
            {isToggling ? (
              <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
            ) : (
              <input
                type="checkbox"
                checked={node.use_as_b2b_filter}
                onChange={() => onToggleB2B(node)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600
                           focus:ring-blue-400 cursor-pointer"
              />
            )}
            <span className="text-xs text-gray-500">מסנן B2B</span>
          </label>
        </td>

        {/* Actions — visible on row hover */}
        <td className="py-2.5 pl-4 pr-2 text-left w-28">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
            <button
              onClick={() => onAddSub(node)}
              title="הוסף תת-קבוצה"
              className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            >
              <FolderPlus className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(node)}
              title="ערוך שם"
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(node)}
              title="מחק"
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>

      {/* Recurse into children when expanded */}
      {isOpen && node.children?.map(child => (
        <GroupNode
          key={child.id}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          toggling={toggling}
          toggle={toggle}
          onAddSub={onAddSub}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleB2B={onToggleB2B}
        />
      ))}
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ProductGroupsSettings({ companyId, showToast }: Props) {
  const [flat,     setFlat]     = useState<ProductGroup[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [toggling, setToggling] = useState<Set<string>>(new Set());

  // Modal state
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editTarget,  setEditTarget]  = useState<ProductGroup | null>(null);
  const [parentGroup, setParentGroup] = useState<ProductGroup | null>(null);
  const [name,        setName]        = useState('');
  const [saving,      setSaving]      = useState(false);
  const [formError,   setFormError]   = useState<string | null>(null);

  // Delete confirm
  const [delTarget,  setDelTarget]  = useState<ProductGroup | null>(null);
  const [deleting,   setDeleting]   = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetch = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error: err } = await supabase
      .from('inventory_product_groups')
      .select('id, company_id, parent_id, name, use_as_b2b_filter, created_at, updated_at')
      .eq('company_id', companyId)
      .order('name');
    if (err) { setError(err.message); }
    else     { setFlat(data ?? []); }
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetch(); }, [fetch]);

  const tree = buildTree(flat);

  // ── Expand / collapse ──────────────────────────────────────────────────────

  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const expandAll   = () => setExpanded(new Set(flat.map(g => g.id)));
  const collapseAll = () => setExpanded(new Set());

  // ── Toggle B2B filter ──────────────────────────────────────────────────────

  const handleToggleB2B = useCallback(async (group: ProductGroup) => {
    setToggling(prev => new Set([...prev, group.id]));
    const newVal = !group.use_as_b2b_filter;
    const { error: err } = await supabase
      .from('inventory_product_groups')
      .update({ use_as_b2b_filter: newVal })
      .eq('id', group.id);
    if (err) {
      showToast(err.message, false);
    } else {
      setFlat(prev =>
        prev.map(g => g.id === group.id ? { ...g, use_as_b2b_filter: newVal } : g),
      );
    }
    setToggling(prev => { const next = new Set(prev); next.delete(group.id); return next; });
  }, [showToast]);

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openAdd = (parent: ProductGroup | null = null) => {
    setEditTarget(null);
    setParentGroup(parent);
    setName('');
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (group: ProductGroup) => {
    setEditTarget(group);
    setParentGroup(null);
    setName(group.name);
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditTarget(null);
    setParentGroup(null);
    setName('');
    setFormError(null);
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) { setFormError('שם הקבוצה הוא שדה חובה'); return; }
    setSaving(true); setFormError(null);
    try {
      if (editTarget) {
        const { error: err } = await supabase
          .from('inventory_product_groups')
          .update({ name: name.trim() })
          .eq('id', editTarget.id);
        if (err) throw err;
        showToast('קבוצה עודכנה בהצלחה');
      } else {
        const { error: err } = await supabase
          .from('inventory_product_groups')
          .insert({
            company_id: companyId,
            parent_id:  parentGroup?.id ?? null,
            name:       name.trim(),
          });
        if (err) throw err;
        if (parentGroup) setExpanded(prev => new Set([...prev, parentGroup.id]));
        showToast('קבוצה נוספה בהצלחה');
      }
      closeModal();
      fetch();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'שגיאה';
      if (msg.toLowerCase().includes('unique')) {
        setFormError('שם קבוצה זה כבר קיים תחת אותו הורה');
      } else {
        showToast(msg, false);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { error: err } = await supabase
      .from('inventory_product_groups')
      .delete()
      .eq('id', delTarget.id);
    if (err) { showToast(err.message, false); }
    else     { showToast('קבוצה נמחקה'); setDelTarget(null); fetch(); }
    setDeleting(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <FolderTree className="w-5 h-5 text-amber-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">קבוצות מוצרים</h2>
            <p className="text-xs text-gray-500">ארגן מוצרים בהיררכיה של קבוצות ותת-קבוצות</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {flat.length > 0 && (
            <>
              <button
                onClick={expandAll}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg transition-colors"
              >
                פתח הכל
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg transition-colors"
              >
                סגור הכל
              </button>
            </>
          )}
          <button
            onClick={() => openAdd(null)}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            הוסף קבוצה
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">טוען קבוצות…</span>
        </div>
      ) : tree.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
          <FolderTree className="w-12 h-12 text-gray-200" />
          <p className="text-sm font-medium">אין קבוצות מוצרים</p>
          <p className="text-xs text-gray-400">לחץ על "הוסף קבוצה" כדי ליצור את הקבוצה הראשונה</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3 text-right">שם הקבוצה</th>
                <th className="px-3 py-3 text-right w-36">
                  <span className="flex items-center gap-1">
                    <Filter className="w-3 h-3 text-blue-400" />
                    מסנן B2B
                  </span>
                </th>
                <th className="px-4 py-3 text-left w-28">פעולות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tree.map(node => (
                <GroupNode
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  toggling={toggling}
                  toggle={toggle}
                  onAddSub={openAdd}
                  onEdit={openEdit}
                  onDelete={setDelTarget}
                  onToggleB2B={handleToggleB2B}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {flat.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400 flex items-center gap-3">
          <span>
            {flat.length} קבוצות · רחף על שורה כדי לראות את הפעולות ·{' '}
            <FolderPlus className="inline w-3 h-3 mx-0.5" /> הוסף תת-קבוצה
          </span>
          {flat.filter(g => g.use_as_b2b_filter).length > 0 && (
            <span className="flex items-center gap-1 text-blue-400">
              <Filter className="w-3 h-3" />
              {flat.filter(g => g.use_as_b2b_filter).length} מסנני B2B פעילים
            </span>
          )}
        </div>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {editTarget
                  ? 'עריכת שם קבוצה'
                  : parentGroup
                    ? `הוספת תת-קבוצה תחת "${parentGroup.name}"`
                    : 'הוספת קבוצה ראשית'}
              </h3>
              <button
                onClick={closeModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {parentGroup && !editTarget && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
                <Folder className="w-3.5 h-3.5 shrink-0" />
                <span>הורה: <strong>{parentGroup.name}</strong></span>
              </div>
            )}

            {formError && (
              <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {formError}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">
                שם הקבוצה <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="לדוגמה: חולצות, מכנסיים, אביזרים…"
                autoFocus
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                           focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              />
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-amber-500 hover:bg-amber-600
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editTarget ? 'שמור שינויים' : 'הוסף קבוצה'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ───────────────────────────────────────────────────── */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת קבוצה</h3>
                <p className="text-sm text-gray-500 mt-1">
                  האם למחוק את הקבוצה{' '}
                  <span className="font-semibold text-gray-800">"{delTarget.name}"</span>?
                  {(delTarget.children?.length ?? 0) > 0 && (
                    <span className="block mt-1 text-amber-600 font-medium">
                      כל תת-הקבוצות שלה יימחקו גם כן.
                    </span>
                  )}
                  {' '}פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDelTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
